import React from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import withAccountFormStepStyles from 'Components/UI/Accounts/AccountApplication/Step/withAccountFormStepStyles';
import MetaField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import Typography from 'Components/UI/Typography';
import { Collapse } from '@mui/material';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import classNames from 'classnames';
import DocumentUploader from 'Components/UI/Accounts/AccountApplication/Step/EmploymentAndOtherInformation/3210DocumentUploader';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';

const hideSecondary = resource => {
  return resource
    .meta()
    .field('secondaryApplicantEmploymentStatus')
    .attr('hidden');
};

const hideInterestedParties = resource => {
  return resource
    .meta()
    .field('hasInterestedParty')
    .attr('hidden');
};

const EMPLOYED = 'EMPLOYED';

function EmploymentAndOtherInformation({ classes }) {
  const { resource, handleChange } = useMetaLayoutContext();

  const hasInterestedParty = resource.attr('hasInterestedParty') === 'true';
  const canChangeAnswerToInterestedPartyQuestion = !resource.meta().field('hasInterestedParty').attr('required');

  const isPrimaryEmployed = resource.attr('primaryApplicantEmploymentStatus') === EMPLOYED;
  const isSecondaryEmployed = resource.attr('secondaryApplicantEmploymentStatus') === EMPLOYED;
  const isPrimaryFinra = resource.attr('primaryApplicantIsFinraAffiliated') === 'true';
  const isPrimaryPoliticallyExposed = resource.attr('primaryApplicantIsPoliticallyExposed') === 'true';
  const isSecondaryPoliticallyExposed = resource.attr('secondaryApplicantIsPoliticallyExposed') === 'true';
  const isSecondaryFinra = resource.attr('secondaryApplicantIsFinraAffiliated') === 'true';

  const isPrimaryControlPerson = resource.attr('primaryApplicantIsControlPerson') === 'true';
  const isSecondaryControlPerson = resource.attr('secondaryApplicantIsControlPerson') === 'true';

  const isSecondaryHidden = hideSecondary(resource);
  const isInterestPartyHidden = hideInterestedParties(resource) && !canChangeAnswerToInterestedPartyQuestion;
  const isJoint = !isSecondaryHidden;

  return (
    <div className={classes.root}>
      {/* <!-- Start Primary Applicant --> */}
      <div test="primary-applicant">
        <div className={classes.row}>
          <Typography
            className={classes.header}
            variant="h3"
            color="primary"
            component={'h2'}
          >
            {`Primary - ${resource.attr('primaryApplicantName')}`}
          </Typography>
        </div>
        <div className={classes.row}>
          <MetaField attr="primaryApplicantEmploymentStatus" />
        </div>
        <Collapse in={isPrimaryEmployed}>
          <Box className={classes.row}>
            <MetaField attr="primaryApplicantEmployer" />
            <MetaField attr="primaryApplicantPositionEmployed" />
          </Box>
        </Collapse>
        <div>
          <Typography
            className={classes.header}
            variant="h3"
            color="primary"
            component={'h3'}
          >
            {'Uncommon Situations'}
          </Typography>
          <Typography variant="h5" color="primaryGray" component={'p'}>
            {
              "For most members, the following situations don't apply - that's why we've already answered \"no\" for you but we still have to take a look."
            }
          </Typography>
        </div>
        <div className={classes.row}>
          <MetaField attr="primaryApplicantIsFinraAffiliated" />
        </div>
        <Collapse in={isPrimaryFinra} mountOnEnter={false}>
          <Box>
            <div className={classes.row}>
              <MetaField attr="primaryApplicantFinraFirmName" />
            </div>
            <DocumentUploader
              onSuccess={async () => {
                try {
                  const nextCollection = await resource
                    .attr('primaryApplicantAttachments')
                    .refresh();
                  handleChange('primaryApplicantAttachments', nextCollection);
                } catch (e) {
                  displayErrorMessage(e, 'Failed to load documents');
                }
              }}
              onDelete={() => {
                handleChange(
                  'primaryApplicantAttachments',
                  resource.attr('primaryApplicantAttachments'),
                );
              }}
              attachments={resource.attr('primaryApplicantAttachments')}
              relationship={resource.relationship(
                fr.relationships.attachPrimary3210Letter.name,
              )}
            />
          </Box>
        </Collapse>
        <div className={classes.row}>
          <MetaField attr="primaryApplicantIsPoliticallyExposed" />
        </div>
        <Collapse in={isPrimaryPoliticallyExposed} mountOnEnter={false}>
          <Box>
            <div className={classes.row}>
              <MetaField attr="primaryApplicantPoliticalOrganization" />
            </div>
            <div className={classes.row}>
              <MetaField attr="primaryApplicantImmediateFamilyPoliticallyExposed" />
            </div>
          </Box>
        </Collapse>
        <div className={classes.row}>
          <MetaField attr="primaryApplicantIsControlPerson" />
        </div>
        <Collapse in={isPrimaryControlPerson}>
          <Box className={classes.row}>
            <MetaField attr="primaryApplicantCompanySymbols" />
          </Box>
        </Collapse>
      </div>
      {/* <!-- End Primary Applicant --> */}

      {/* <!-- Start Secondary Applicant --> */}
      <div
        test="secondary-applicant"
        className={classNames({
          [classes.hidden]: isSecondaryHidden,
        })}
      >
        <div
          className={classNames(classes.row, {
            [classes.hidden]: isSecondaryHidden,
          })}
        >
          <Typography
            className={classes.header}
            variant="h3"
            color="primary"
            component={'h2'}
          >
            {`Secondary - ${resource.attr('secondaryApplicantName')}`}
          </Typography>
        </div>
        <div className={classes.row}>
          <MetaField attr="secondaryApplicantEmploymentStatus" />
        </div>
        <Collapse in={isSecondaryEmployed}>
          <Box className={classes.row}>
            <MetaField attr="secondaryApplicantEmployer" />
            <MetaField attr="secondaryApplicantPositionEmployed" />
          </Box>
        </Collapse>
        <div>
          <Typography
            className={classes.header}
            variant="h3"
            color="primary"
            component={'h3'}
          >
            {'Uncommon Situations'}
          </Typography>
        </div>
        <div className={classes.row}>
          <MetaField attr="secondaryApplicantIsFinraAffiliated" />
        </div>
        <Collapse in={isSecondaryFinra}>
          <Box>
            <div className={classes.row}>
              <MetaField attr="secondaryApplicantFinraFirmName" />
            </div>
            {resource.relationship(
              fr.relationships.attachSecondary3210Letter.name,
            ) && (
              <DocumentUploader
                onSuccess={async () => {
                  try {
                    const nextCollection = await resource
                      .attr('secondaryApplicantAttachments')
                      .refresh();
                    handleChange(
                      'secondaryApplicantAttachments',
                      nextCollection,
                    );
                  } catch (e) {
                    displayErrorMessage(e, 'Failed to load documents');
                  }
                }}
                onDelete={() => {
                  handleChange(
                    'secondaryApplicantAttachments',
                    resource.attr('secondaryApplicantAttachments'),
                  );
                }}
                attachments={resource.attr('secondaryApplicantAttachments')}
                relationship={resource.relationship(
                  fr.relationships.attachSecondary3210Letter.name,
                )}
              />
            )}
          </Box>
        </Collapse>
        <div className={classes.row}>
          <MetaField attr="secondaryApplicantIsPoliticallyExposed" />
        </div>
        <Collapse in={isSecondaryPoliticallyExposed}>
          <Box>
            <div className={classes.row}>
              <MetaField attr="secondaryApplicantPoliticalOrganization" />
            </div>
            <div className={classes.row}>
              <MetaField attr="secondaryApplicantImmediateFamilyPoliticallyExposed" />
            </div>
          </Box>
        </Collapse>
        <div className={classes.row}>
          <MetaField attr="secondaryApplicantIsControlPerson" />
        </div>
        <Collapse in={isSecondaryControlPerson}>
          <Box className={classes.row}>
            <MetaField attr="secondaryApplicantCompanySymbols" />
          </Box>
        </Collapse>
      </div>
      {/* <!-- End Secondary Applicant --> */}

      {/* <!-- Start Backup Withholding --> */}
      {isJoint && (
      <div>
        <Typography
          className={classes.header}
          variant="h3"
          color="primary"
          component={'h3'}
        >
          {`${resource.attr('primaryApplicantName')} & ${resource.attr('secondaryApplicantName')}`}
        </Typography>
      </div>
      )}
      <div className={classes.row}>
        <MetaField attr="subjectToBackUpWithholding" />
      </div>

      {/* <!-- Start Interested Party --> */}
      <div className={classNames('', {
        [classes.hidden]: isInterestPartyHidden,
      })}
      >
        <div
          className={classes.row}
        >
          <Typography
            className={classes.header}
            variant="h3"
            color="primary"
            component={'h2'}
          >
            {'Interested Party'}
          </Typography>
        </div>
        { canChangeAnswerToInterestedPartyQuestion
          ? (
            <Typography variant="subtitle1" className={classes.changeInformationText}>
              {resource.attr('interestedPartyChangeInformation')}
            </Typography>)
          : (
            <div className={classes.row}>
              <MetaField attr="hasInterestedParty" />
            </div>)
      }
        <Collapse in={hasInterestedParty}>
          <div>
            <div className={classes.row}>
              <MetaField attr="interestedPartyCompanyName" />
            </div>
            <div className={classes.row}>
              <MetaField attr="interestedPartyAddressLine1" />
              <MetaField attr="interestedPartyAddressLine2" />
            </div>
            <div className={classes.row}>
              <MetaField attr="interestedPartyCity" />
              <MetaField attr="interestedPartyState" />
            </div>
            <div className={classes.row}>
              <MetaField attr="interestedPartyZipCode" />
              <MetaField attr="interestedPartyCountry" />
            </div>
          </div>
        </Collapse>
      </div>
      {/* <!-- End Interested Party --> */}


    </div>
  );
}

EmploymentAndOtherInformation.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withAccountFormStepStyles(EmploymentAndOtherInformation);
